import type { IPosition } from '../types/types';
import type { PlacedLocation } from '../types/map.types';
import { mapService } from './MapService';
import { dungeonService } from './DungeonService';
import { findDropCell } from '../utils/map.utils';

interface LocationSpawnRequest {
  kind: 'location';
  location: PlacedLocation;
}

interface SealRequest {
  kind: 'seal';
  cells: IPosition[];
}

type PendingRequest = LocationSpawnRequest | SealRequest;

/**
 * Single channel for post-generation grid mutations (dynamic location spawn +
 * tileset seal). Guarantees cell-occupancy integrity. Initial floor generation
 * does NOT go through this service. See docs/locations.md — § Spawn dynamique sûr.
 */
class DynamicPlacementService {
  private pending: PendingRequest[] = [];

  /** Set by Dungeon.tsx (host) — replicates a runtime location spawn (challenge
   * reward chest) to clients. Host-guarded inside the callback; no-op off host. */
  public onLocationSpawned: (location: PlacedLocation) => void = () => {};

  public hasPending(): boolean {
    return this.pending.length > 0;
  }

  public clear(): void {
    this.pending = [];
  }

  public requestLocationSpawn(location: PlacedLocation): void {
    this.pending.push({ kind: 'location', location });
    this.process();
  }

  public requestTileChange(
    cells: IPosition[],
    toType: 'wall' | 'restore',
  ): void {
    if (toType === 'restore') {
      // wall -> ground never traps: apply immediately, and cancel any pending
      // seal on these cells (escape race: a door waiting to seal must not seal
      // after the challenge ended). `cells` must cover the original seal set so
      // restoreCells also undoes cells already sealed in a prior partial tick.
      this.cancelPendingSeal(cells);
      mapService.restoreCells(cells);
      return;
    }
    this.pending.push({ kind: 'seal', cells: [...cells] });
    this.process();
  }

  public tick(): void {
    if (this.pending.length === 0) {
      return;
    }
    this.process();
  }

  private process(): void {
    const next: PendingRequest[] = [];
    for (const req of this.pending) {
      if (req.kind === 'location') {
        if (!this.tryLocation(req.location)) {
          next.push(req);
        }
      } else {
        const remaining = this.trySeal(req.cells);
        if (remaining.length > 0) {
          next.push({ kind: 'seal', cells: remaining });
        }
      }
    }
    this.pending = next;
  }

  private footprintCells(loc: PlacedLocation): IPosition[] {
    const cells: IPosition[] = [];
    for (let x = loc.x; x < loc.x + loc.width; x++) {
      for (let y = loc.y; y > loc.y - loc.height; y--) {
        cells.push({ x, y });
      }
    }
    return cells;
  }

  private keyOf(cells: IPosition[]): Set<string> {
    return new Set(cells.map((c) => `${c.x},${c.y}`));
  }

  /** Move loot off each cell to the nearest valid cell outside `cells`. */
  private relocateLootOff(cells: IPosition[]): void {
    const excluded = this.keyOf(cells);
    const inv = dungeonService.inventoryService;
    let moved = false;
    for (const c of cells) {
      if (inv.getItemsAt(c.x, c.y).length === 0) {
        continue;
      }
      const target = findDropCell(c.x, c.y, (tx, ty) =>
        excluded.has(`${tx},${ty}`),
      );
      if (!target) {
        // eslint-disable-next-line no-console
        console.warn(
          `[DynamicPlacement] no free cell to relocate loot off (${c.x},${c.y}); leaving in place`,
        );
        continue;
      }
      inv.moveGroundItems(c.x, c.y, target.x, target.y);
      moved = true;
    }
    if (moved) {
      inv.triggerGroundPickup();
    }
  }

  /** @returns true when the location was placed or the request must be dropped. */
  private tryLocation(loc: PlacedLocation): boolean {
    const cells = this.footprintCells(loc);
    for (const c of cells) {
      if (mapService.getTileAt(c.x, c.y)?.type !== 'ground') {
        // eslint-disable-next-line no-console
        console.warn(
          `[DynamicPlacement] cannot spawn '${loc.id}' on non-ground cell (${c.x},${c.y}); cancelled`,
        );
        return true;
      }
      if (dungeonService.locationService.isLocationAt(c.x, c.y)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[DynamicPlacement] cannot spawn '${loc.id}' on existing location at (${c.x},${c.y}); cancelled`,
        );
        return true;
      }
    }
    this.relocateLootOff(cells);
    for (const c of cells) {
      if (dungeonService.getEntityAt(c.x, c.y)) {
        return false; // entity present: defer to next tick
      }
    }
    dungeonService.locationService.addPlacedLocation(loc);
    this.onLocationSpawned(loc);
    return true;
  }

  /** @returns cells still entity-blocked (to keep pending). */
  private trySeal(cells: IPosition[]): IPosition[] {
    this.relocateLootOff(cells);
    const toSeal: IPosition[] = [];
    const stillBlocked: IPosition[] = [];
    for (const c of cells) {
      if (dungeonService.locationService.isLocationAt(c.x, c.y)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[DynamicPlacement] skip sealing location cell (${c.x},${c.y})`,
        );
        continue;
      }
      if (dungeonService.getEntityAt(c.x, c.y)) {
        stillBlocked.push(c);
        continue;
      }
      toSeal.push(c);
    }
    if (toSeal.length > 0) {
      mapService.sealCells(toSeal);
    }
    return stillBlocked;
  }

  private cancelPendingSeal(cells: IPosition[]): void {
    const excluded = this.keyOf(cells);
    const next: PendingRequest[] = [];
    for (const req of this.pending) {
      if (req.kind !== 'seal') {
        next.push(req);
        continue;
      }
      const kept = req.cells.filter((c) => !excluded.has(`${c.x},${c.y}`));
      if (kept.length > 0) {
        next.push({ kind: 'seal', cells: kept });
      }
    }
    this.pending = next;
  }
}

export const dynamicPlacementService = new DynamicPlacementService();
